/**
 * AccountPlanSection — het Full Frame Klant Canvas (accountplan) per account:
 * kernteam, beslissers, opdracht, actieplan, kansen/bedreigingen, tevredenheid,
 * target & business, werkzaamheden, succes en marktmomenten. Geïmporteerd uit
 * de KlantCanvas-documenten; hier direct bij te werken (opslaan bij blur).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper, Typography, Stack, TextField, Grid, CircularProgress, Chip, Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// Sommige geïmporteerde velden bevatten "snake_case: waarde"-regels (bv.
// korte_omschrijving / business_goals_langere_termin). Toon die als nette labels.
const humanizeKey = (k) => k.replace(/_/g, ' ').replace(/\blangere termin\b/i, 'langere termijn')
  .replace(/^\w/, (c) => c.toUpperCase());

function PrettyText({ text }) {
  const lines = String(text || '').split('\n');
  const out = [];
  lines.forEach((ln, i) => {
    const m = ln.match(/^\s*([a-z][a-z0-9_]{2,}):\s*(.*)$/);
    if (m) {
      out.push(
        <Box key={i} sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', textTransform: 'none' }}>{humanizeKey(m[1])}</Typography>
          {m[2] && <Typography variant="body2" sx={{ fontSize: 13.5, lineHeight: 1.6 }}>{m[2]}</Typography>}
        </Box>,
      );
    } else if (ln.trim()) {
      out.push(<Typography key={i} variant="body2" sx={{ fontSize: 13.5, lineHeight: 1.6, mb: 0.5 }}>{ln}</Typography>);
    }
  });
  return out.length ? <>{out}</> : <Typography variant="body2" color="text.disabled">—</Typography>;
}

// Lees-weergave (opgemaakt); klik of ✎ → bewerken; blur → opslaan.
function FieldEditor({ label, value, onChange, onSave }) {
  const [editing, setEditing] = useState(false);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{label}</Typography>
        {!editing && (
          <EditOutlinedIcon onClick={() => setEditing(true)}
            sx={{ fontSize: 15, color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#475569' } }} />
        )}
      </Stack>
      {editing ? (
        <TextField fullWidth multiline minRows={2} maxRows={14} size="small" autoFocus
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => { onSave(); setEditing(false); }}
          placeholder="—" sx={{ '& textarea': { fontSize: 13.5, lineHeight: 1.6 } }} />
      ) : (
        <Box onClick={() => setEditing(true)}
          sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid #eef2f6', bgcolor: '#fafbfc', cursor: 'text', minHeight: 40 }}>
          <PrettyText text={value} />
        </Box>
      )}
    </Box>
  );
}

const SECTIONS = [
  { key: 'kernteam', label: '👥 Kernteam', half: true },
  { key: 'beslissers', label: '🧭 Beslissers & beïnvloeders', half: true },
  { key: 'opdracht', label: '🎯 Onze opdracht & business goals' },
  { key: 'actieplan', label: '🚀 Actieplan' },
  { key: 'kansen', label: '📈 Kansen', half: true },
  { key: 'bedreigingen', label: '⚠️ Bedreigingen', half: true },
  { key: 'klanttevredenheid', label: '💚 Klanttevredenheid', half: true },
  { key: 'target_business', label: '💶 Target & business', half: true },
  { key: 'type_werkzaamheden', label: '🎬 Type werkzaamheden', half: true },
  { key: 'succes', label: '🥂 Hoe ziet succes eruit?', half: true },
  { key: 'marktmomenten', label: '📅 Belangrijke data & marktmomenten' },
];

export default function AccountPlanSection({ accountId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const savedRef = useRef({});

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/plan`);
      if (r.ok) {
        const d = await r.json();
        setPlan(d);
        savedRef.current = { ...d };
      }
    } catch { /* optionele sectie */ }
    finally { setLoading(false); }
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const saveField = async (key) => {
    if ((savedRef.current[key] || '') === (plan?.[key] || '')) return;
    savedRef.current[key] = plan?.[key] || '';
    try {
      await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/plan`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: plan?.[key] || '' }),
      });
    } catch { /* volgende blur probeert opnieuw */ }
  };

  if (loading) return null;
  const filled = SECTIONS.filter((s) => (plan?.[s.key] || '').trim()).length;

  return (
    <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} onClick={() => setOpen((v) => !v)} sx={{ cursor: 'pointer' }}>
        <ExpandMoreIcon sx={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s', color: 'text.secondary' }} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>📋 Accountplan</Typography>
        <Chip size="small" label={filled ? `${filled}/${SECTIONS.length} ingevuld` : 'nog leeg'}
          color={filled ? 'success' : 'default'} sx={{ fontWeight: 700 }} />
        {plan?.source_file && (
          <Typography variant="caption" color="text.disabled">bron: {plan.source_file}</Typography>
        )}
      </Stack>
      {open && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {SECTIONS.map((s) => (
            <Grid item xs={12} md={s.half ? 6 : 12} key={s.key}>
              <FieldEditor label={s.label} value={plan?.[s.key] || ''}
                onChange={(v) => setPlan((p) => ({ ...p, [s.key]: v }))}
                onSave={() => saveField(s.key)} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Box>
              <Typography variant="caption" color="text.disabled">
                Wijzigingen worden automatisch opgeslagen zodra je uit een veld klikt.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}
